import { useEffect } from 'react';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  DrawerContent,
  DrawerFooter,
  Icons,
  notify,
  PreviewSnippet,
  Skeleton,
  useDrawerInstance,
  useSwipeableViews,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { getDocsLink } from '@cast/utils';

import { useGetEdgeInitdOnboardingScriptQuery } from 'hooks/queries/omni';

import { buildEdgeInitdConfig } from './buildEdgeInitdConfig';
import { useProvisionNodesDrawerContext } from '../ProvisionNodesDrawerContext';
import { ProvisionNodesDrawerSlides } from '../types';

const SetupFooter = () => {
  const { close, setOnCloseGuard, setShouldRenderGuard } = useDrawerInstance();
  const swipeableViews = useSwipeableViews();
  const { customConfigValue } = useProvisionNodesDrawerContext();

  // Enable guard when we have config values
  useEffect(() => {
    setOnCloseGuard(!!customConfigValue);
  }, [customConfigValue, setOnCloseGuard]);

  const handleClose = () => {
    if (customConfigValue) {
      setShouldRenderGuard(true);
    } else {
      close();
    }
  };

  return (
    <DrawerFooter sx={{ justifyContent: 'space-between' }}>
      <Button
        variant="secondary"
        size="md"
        startIcon={<Icons.ArrowLeft />}
        onClick={() => swipeableViews?.goToPreviousSlide()}
        testId="back-button"
      >
        Back
      </Button>
      <Stack direction="row" gap={16}>
        <Button
          variant="secondary"
          size="md"
          onClick={handleClose}
          testId="cancel-button"
        >
          Cancel
        </Button>
        <Button size="md" onClick={() => close()} testId="done-button">
          Done
        </Button>
      </Stack>
    </DrawerFooter>
  );
};

export const ProvisionNodesSetupSlide = () => {
  const { clusterId, edgeLocation, customConfigValue } =
    useProvisionNodesDrawerContext();
  const swipeableViews = useSwipeableViews();

  const isActive =
    swipeableViews?.currentSlide === ProvisionNodesDrawerSlides.Setup;
  const edgeLocationId = edgeLocation?.id;
  // TODO(UI-6622): Currently fails for non-custom edge locations; fix pending in ticket.
  const { script, isLoading } = useGetEdgeInitdOnboardingScriptQuery({
    clusterId: clusterId,
    edgeLocationId: edgeLocationId,
    config: buildEdgeInitdConfig(customConfigValue),
    enabled: isActive && !!clusterId && !!edgeLocationId && !!customConfigValue,
  });

  return (
    <>
      <DrawerContent gap={16} flex={1}>
        <Stack gap={4}>
          <Typography variant="P14R">
            Run this script on the VM or bare metal machine you want to attach
            as an edge node. {CAST_AI} discovers the node&apos;s metadata, and
            the node appears in your Node list automatically. Requires network
            connectivity between your provider and the cluster.
          </Typography>
          <ExternalLink link={getDocsLink('docs_omni_custom_edge_location')}>
            Documentation
          </ExternalLink>
        </Stack>
        <Stack gap={8}>
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
          >
            Run the script:
          </Typography>
          {isLoading ? (
            <Skeleton
              height={120}
              data-testid="provision-nodes-setup-script-skeleton"
            />
          ) : (
            <PreviewSnippet
              code={script || ''}
              onCopy={() => notify.success('Copied to clipboard')}
              testId="provision-nodes-setup-script"
            />
          )}
        </Stack>
      </DrawerContent>
      <SetupFooter />
    </>
  );
};
